import React, { useState, useEffect, useMemo, forwardRef, createContext, useContext, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { 
  Check, Clock, Shield, Globe, Zap, Bot, Eye, EyeOff, 
  Database, Cpu, ChevronRight, Terminal, Activity, Menu, X, ChevronDown,
  Phone, BarChart3, AlertCircle, Play, Pause, Plus, Trash2, FileText, Settings,
  Lock, UserCheck, Banknote, XCircle, Fingerprint, Mic, MessageSquare, Layers, Code,
  Wifi, Search, Share2, Command
} from "lucide-react";
import WorkflowMap from './components/WorkflowMap';

// --- NAVIGATION CONTEXT ---
const NavigationContext = createContext({
  currentView: 'landing',
  navigate: (view) => {},
  user: null,
  login: () => {},
  logout: () => {}
});

const useNavigation = () => useContext(NavigationContext);

// --- UI PRIMITIVES ---

const cn = (...classes) => classes.filter(Boolean).join(" ");

const Button = forwardRef(({ className = "", variant = "default", size = "default", ...props }, ref) => {
  const baseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-500 disabled:pointer-events-none disabled:opacity-50 cursor-pointer active:scale-95";
  const variants = {
    default: "bg-white text-black shadow hover:bg-cyan-50",
    outline: "border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 text-white",
    ghost: "hover:bg-white/10 text-white",
    glow: "bg-cyan-500/10 text-cyan-400 border border-cyan-500/50 shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.5)] hover:bg-cyan-500/20",
  };
  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-12 rounded-md px-8 text-base",
    icon: "h-9 w-9",
  };
  return (
    <button
      ref={ref}
      className={cn(baseStyles, variants[variant] || variants.default, sizes[size] || sizes.default, className)}
      {...props}
    />
  );
});
Button.displayName = "Button";

const Input = forwardRef(({ className, type, ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-white/10 bg-black/40 px-3 py-1 text-sm shadow-inner transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-50 text-white",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
Input.displayName = "Input";

const Badge = ({ className = "", variant = "default", ...props }) => {
  const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
  const variants = {
    default: "border-transparent bg-cyan-500 text-black shadow hover:bg-cyan-500/80",
    outline: "text-white border-white/20",
    live: "border-red-500/30 bg-red-500/10 text-red-400 animate-pulse",
  };
  return (
    <div className={cn(baseStyles, variants[variant] || variants.default, className)} {...props} />
  );
};

const AccordionContext = createContext({});
const Accordion = ({ children, className, ...props }) => {
  const [openItem, setOpenItem] = useState(null);
  return (
    <AccordionContext.Provider value={{ openItem, setOpenItem }}>
      <div className={cn("space-y-4", className)} {...props}>{children}</div>
    </AccordionContext.Provider>
  );
};
const AccordionItem = ({ children, value, className }) => {
  return <div className={cn("border-b border-white/10", className)}>{React.Children.map(children, child => React.cloneElement(child, { value }))}</div>;
};
const AccordionTrigger = ({ children, value, className }) => {
  const { openItem, setOpenItem } = useContext(AccordionContext);
  const isOpen = openItem === value;
  return (
    <button
      onClick={() => setOpenItem(isOpen ? null : value)}
      className={cn("flex flex-1 items-center justify-between py-4 font-medium transition-all hover:text-cyan-300 text-left w-full text-white/80 hover:text-white", className)}
    >
      {children}
      <ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-200", isOpen && "rotate-180")} />
    </button>
  );
};
const AccordionContent = ({ children, value, className }) => {
  const { openItem } = useContext(AccordionContext);
  if (openItem !== value) return null;
  return (
    <div className={cn("overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down", className)}>
      <div className="pb-4 pt-0 text-white/60 leading-relaxed">{children}</div>
    </div>
  );
};

// --- 3D ASSETS & VISUALS ---

const GT = ({ children }) => (
  <span className="bg-gradient-to-r from-cyan-300 via-fuchsia-400 to-emerald-300 bg-clip-text text-transparent font-bold">{children}</span>
);

const Glow = ({ className = "" }) => (
  <div className={`pointer-events-none absolute inset-0 -z-10 [mask-image:radial-gradient(closest-side,white,transparent)] ${className}`} />
);

// --- CORE: EasyDialLogo (RESTORED ORIGINAL) ---
export function EasyDialLogo({
  rotation = 0,
  mainCircleStroke = "#ffffff",
  mainCircleOpacity = 0.12,
  mainCircleStrokeWidth = 10,
  arcStrokeWidth = 7,
  centerDotColor = "#ffffff",
  centerDotRadius = 5,
  lineColor = "#ffffff",
  lineOpacity = 0.9,
  lineStrokeWidth = 4,
  glow = true,
  className = "h-14 w-14 md:h-16 md:w-16",
}) {
  return (
    <svg viewBox="0 0 96 96" className={className} xmlns="http://www.w3.org/2000/svg" aria-label="EasyDial">
      <defs>
        <radialGradient id="edGlowGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60efff" stopOpacity="0.35" />
          <stop offset="55%" stopColor="#b06ab3" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#00d98b" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="edArcGrad" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="50%" stopColor="#d946ef" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
      </defs>
      {glow && <circle cx="48" cy="48" r="34" fill="url(#edGlowGrad)" />}
      <g>
        <g transform={`rotate(${rotation} 48 48)`}>
          <circle cx="48" cy="48" r="28" fill="none" stroke={mainCircleStroke} strokeOpacity={mainCircleOpacity} strokeWidth={mainCircleStrokeWidth} />
          <path d="M48 24 A24 24 0 0 0 48 72" fill="none" stroke="url(#edArcGrad)" strokeWidth={arcStrokeWidth} strokeLinecap="round" />
          <circle cx="48" cy="48" r={centerDotRadius} fill={centerDotColor} />
          <path d="M 53.2 45 L 69.5 35.5 M 42.8 51 L 32.76 56.8" stroke={lineColor} strokeOpacity={lineOpacity} strokeWidth={lineStrokeWidth} strokeLinecap="round" />
        </g>
      </g>
    </svg>
  );
}

// --- NEURAL COMMAND CENTER ---

const NeuralCommandCenter = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const [isFraud, setIsFraud] = useState(false); // Re-purposed as "isConflict" or "isAlert"
  const [messages, setMessages] = useState([]);
  
  // 3D Physics Hooks
  const springConfig = { damping: 30, stiffness: 200 };
  const rotateX = useSpring(useMotionValue(0), springConfig);
  const rotateY = useSpring(useMotionValue(0), springConfig);

  // Conversation Script - APPOINTMENT REMINDER
  const script = useMemo(() => [
    { role: 'sys', text: "DIALING: +1 (555) 019-2834..." },
    { role: 'sys', text: "CONNECTION ESTABLISHED via SIP-TRUNK-04" },
    { role: 'ai', text: "Hi Alex. This is the EasyDial assistant for Dr. Chen's Dental. Calling to remind you of your cleaning tomorrow at 9 AM." },
    { role: 'user', text: "Ah, shoot. I completely forgot. I have a meeting then." },
    { role: 'sys', text: "⚠ CONFLICT DETECTED: SCHEDULE_CLASH" }, // Trigger Alert
    { role: 'sys', text: "⚠ INTENT: RESCHEDULE" },
    { role: 'ai', text: "No problem. I see an opening at 2 PM tomorrow or 10 AM Friday. Do either of those work for you?" },
    { role: 'user', text: "2 PM tomorrow is perfect actually." },
    { role: 'ai', text: "Great. I've moved your appointment to 2 PM tomorrow. You'll get a text confirmation shortly." },
    { role: 'sys', text: "APPOINTMENT_CONFIRMED: ID #9928-X" }, // Resolve Alert
    { role: 'ai', text: "Have a great day, Alex!" },
  ], []);

  // Parallax Logic
  const handleMouseMove = (e) => {
    if(!containerRef.current) return;
    const { width, height, left, top } = containerRef.current.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    
    // Rotate visual based on mouse position (Inverse interaction)
    rotateY.set(mouseX * 0.02); 
    rotateX.set(mouseY * -0.02);
    setMouse({ x: mouseX, y: mouseY });
  };

  // Loop Simulation
  useEffect(() => {
    let step = 0;
    let timeout;

    const playScript = () => {
        if (step >= script.length) {
            timeout = setTimeout(() => {
                setMessages([]);
                setIsFraud(false);
                step = 0;
                playScript();
            }, 5000);
            return;
        }

        const msg = script[step];
        setMessages(prev => [...prev, msg]);
        
        // Trigger effects based on content (Re-using 'isFraud' state logic for conflict)
        if (msg.text.includes("CONFLICT DETECTED")) setIsFraud(true);
        if (msg.text.includes("APPOINTMENT_CONFIRMED")) setIsFraud(false);

        step++;
        // Variable timing for realism
        const delay = msg.role === 'sys' ? 1000 : msg.role === 'user' ? 2000 : 2500; 
        timeout = setTimeout(playScript, delay);
    };

    playScript();
    return () => clearTimeout(timeout);
  }, [script]);

  return (
    <section 
        ref={containerRef}
        onMouseMove={handleMouseMove}
        // CHANGED: Background color to match website theme (#05060a)
        className="relative w-full h-[850px] bg-[#05060a] overflow-hidden flex items-center justify-center perspective-[1200px]"
    >
        {/* --- ENVIRONMENT --- */}
        
        {/* Ambient Glows */}
        <motion.div 
            animate={{ opacity: isFraud ? 0 : 0.3 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-cyan-500/20 blur-[120px] rounded-full pointer-events-none mix-blend-screen transition-opacity duration-1000" 
        />
        <motion.div 
            animate={{ opacity: isFraud ? 0.4 : 0 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-red-600/30 blur-[120px] rounded-full pointer-events-none mix-blend-screen transition-opacity duration-200" 
        />

        {/* --- 3D STAGE CONTAINER --- */}
        <motion.div 
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative w-full max-w-5xl h-[600px] flex items-center justify-center"
        >
            
            {/* --- CENTRAL AI CORE (THE GYROSCOPE) --- */}
            <div 
                className="absolute w-64 h-64 flex items-center justify-center"
                style={{ transform: "translateZ(50px) scale(0.7)", transformStyle: "preserve-3d" }}
            >
                {/* Core Sphere */}
                <motion.div 
                    animate={{ 
                        boxShadow: isFraud ? "0 0 80px rgba(239,68,68,0.6)" : "0 0 50px rgba(34,211,238,0.3)",
                        borderColor: isFraud ? "rgba(239,68,68,0.8)" : "rgba(34,211,238,0.5)"
                    }}
                    className="absolute inset-0 rounded-full border-2 bg-black/60 backdrop-blur-sm flex items-center justify-center transition-colors duration-500"
                >
                    {/* Larger Bot Icon (w-28 h-28) */}
                    <Bot className={`w-28 h-28 transition-colors duration-500 ${isFraud ? 'text-red-500' : 'text-cyan-400'}`} />
                </motion.div>

                {/* Ring 1 - Fast X Rotation */}
                <motion.div 
                    animate={{ rotateX: 360, rotateY: 15 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    className={`absolute -inset-4 rounded-full border border-dashed transition-colors duration-500 ${isFraud ? 'border-red-500/40' : 'border-cyan-500/30'}`}
                />

                {/* Ring 2 - Slow Y Rotation */}
                <motion.div 
                    animate={{ rotateY: -360, rotateZ: 10 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    className={`absolute -inset-12 rounded-full border border-dotted transition-colors duration-500 ${isFraud ? 'border-red-500/30' : 'border-cyan-500/20'}`}
                />

                {/* Ring 3 - Off-Axis Scanner */}
                <motion.div 
                    animate={{ rotateZ: 360, rotateX: 60 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                    className="absolute -inset-20 rounded-full border border-white/5"
                >
                    <div className={`absolute top-0 left-1/2 w-3 h-3 rounded-full shadow-[0_0_15px_currentColor] transition-colors duration-500 ${isFraud ? 'bg-red-500 text-red-500' : 'bg-cyan-400 text-cyan-400'}`} />
                </motion.div>
            </div>


            {/* --- FLOATING PANEL: LIVE TRANSCRIPT (RIGHT) --- */}
            <motion.div 
                className="absolute right-0 top-12 w-96 h-[450px] bg-[#050505]/90 border border-white/10 rounded-xl backdrop-blur-xl overflow-hidden flex flex-col shadow-[0_0_50px_-10px_rgba(0,0,0,0.8)]"
                style={{ 
                    transform: "translateX(5%) translateZ(100px) rotateY(-12deg)", 
                    transformStyle: "preserve-3d"
                }}
            >
                {/* Panel Header */}
                <div className={`p-4 border-b border-white/10 flex justify-between items-center transition-colors duration-500 ${isFraud ? 'bg-red-500/20' : 'bg-cyan-500/10'}`}>
                    <div className="flex items-center gap-3">
                         <div className={`w-2 h-2 rounded-full animate-pulse ${isFraud ? 'bg-red-500' : 'bg-green-500'}`} />
                         {/* Changed font-mono to font-sans */}
                         <span className="font-sans text-xs font-bold tracking-widest text-white">LIVE_NEURAL_FEED</span>
                    </div>
                    <Activity className={`w-4 h-4 ${isFraud ? 'text-red-500' : 'text-cyan-500'}`} />
                </div>

                {/* Transcript Area */}
                {/* Changed font-mono to font-sans */}
                <div className="flex-1 p-5 space-y-4 overflow-y-auto font-sans text-sm scrollbar-hide">
                    <AnimatePresence mode='popLayout'>
                        {messages.map((msg, i) => (
                            <motion.div 
                                key={i}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                            >
                                {msg.role === 'sys' ? (
                                    <div className={`w-full border-l-2 pl-3 py-1 my-2 text-[10px] ${msg.text.includes('CONFLICT') || msg.text.includes('INTENT') ? 'border-red-500 text-red-400 bg-red-500/5' : 'border-cyan-500/50 text-cyan-400 bg-cyan-500/5'}`}>
                                        {msg.text}
                                    </div>
                                ) : (
                                    <>
                                        <span className="text-[10px] uppercase text-white/40 mb-1 font-semibold">{msg.role === 'ai' ? 'EasyDial Agent' : 'User'}</span>
                                        <div className={`p-3 rounded-lg max-w-[90%] ${
                                            msg.role === 'ai' 
                                            ? 'bg-white/10 text-white border border-white/10' 
                                            : 'bg-cyan-900/30 text-cyan-100 border border-cyan-500/30'
                                        }`}>
                                            {msg.text}
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
                
                {/* Floating decorative elements on the glass */}
                {/* Changed font-mono to font-sans */}
                <div className="absolute bottom-4 right-4 text-[10px] text-white/30 font-sans">ENC: AES-256-GCM</div>
            </motion.div>


            {/* --- FLOATING PANEL: ANALYTICS (LEFT) --- */}
            <motion.div 
                className="absolute left-0 top-24 w-80 h-auto"
                style={{ 
                    transform: "translateX(10%) translateZ(80px) rotateY(12deg)",
                    transformStyle: "preserve-3d"
                }}
            >
                 {/* Card 1: Voice Biometrics */}
                 <div className="mb-4 bg-[#050505]/90 border border-white/10 rounded-xl p-4 backdrop-blur-xl shadow-lg">
                    <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase mb-3">
                        <Fingerprint className="w-4 h-4 text-cyan-400" />
                        Voice Biometrics
                    </div>
                    <div className="flex items-end gap-1 h-16 mb-2">
                        {[40, 70, 30, 85, 60, 90, 45, 75, 50, 80, 20, 60].map((h, i) => (
                            <motion.div 
                                key={i}
                                className={`flex-1 rounded-t-sm ${isFraud ? 'bg-red-500' : 'bg-cyan-500'}`}
                                animate={{ height: [h+"%", (Math.random()*80+10)+"%", h+"%"] }}
                                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse", delay: i * 0.05 }}
                            />
                        ))}
                    </div>
                    {/* Changed font-mono to font-sans */}
                    <div className="flex justify-between text-[10px] font-sans text-white/60">
                        <span>MATCH: 98.2%</span>
                        <span>ID: VERIFIED</span>
                    </div>
                 </div>

                 {/* Card 2: Sentiment Analysis */}
                 <div className={`mb-4 bg-[#050505]/90 border rounded-xl p-4 backdrop-blur-xl shadow-lg transition-colors duration-500 ${isFraud ? 'border-red-500/30' : 'border-white/10'}`}>
                    <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase mb-3">
                        <Activity className={`w-4 h-4 ${isFraud ? 'text-red-500' : 'text-emerald-400'}`} />
                        Schedule Health
                    </div>
                    <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden mb-2">
                        <motion.div 
                            className={`h-full ${isFraud ? 'bg-red-500' : 'bg-emerald-400'}`}
                            animate={{ width: isFraud ? "85%" : "20%" }}
                            transition={{ duration: 1 }}
                        />
                    </div>
                    {/* Changed font-mono to font-sans */}
                    <div className="text-right text-[10px] font-sans text-white/60">
                        {isFraud ? "CONFLICT / RESOLUTION REQUIRED" : "OPTIMAL / CONFIRMED"}
                    </div>
                 </div>

                 {/* Card 3: Intention Analysis (NEW) */}
                 <div className="bg-[#050505]/90 border border-white/10 rounded-xl p-4 backdrop-blur-xl shadow-lg transition-colors duration-500">
                    <div className="flex items-center gap-2 text-white/80 text-xs font-bold uppercase mb-3">
                        <Zap className={`w-4 h-4 ${isFraud ? 'text-purple-400' : 'text-cyan-400'}`} />
                        Live Intention
                    </div>
                    <div className="space-y-3">
                         <div>
                            {/* Changed font-mono to font-sans */}
                            <div className="flex justify-between text-[10px] font-sans text-white/60 mb-1">
                                <span>{isFraud ? "RESCHEDULE" : "CONFIRMATION"}</span>
                                <span className={isFraud ? "text-purple-400" : "text-cyan-400"}>
                                    {isFraud ? "92%" : "98%"}
                                </span>
                            </div>
                            <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                                <motion.div 
                                    className={`h-full ${isFraud ? 'bg-purple-500' : 'bg-cyan-500'}`}
                                    animate={{ width: isFraud ? "92%" : "98%" }}
                                    transition={{ duration: 0.5 }}
                                />
                            </div>
                         </div>
                         
                         {/* Secondary Intent (Faded) */}
                         <div>
                            {/* Changed font-mono to font-sans */}
                            <div className="flex justify-between text-[10px] font-sans text-white/30 mb-1">
                                <span>{isFraud ? "CANCEL" : "INQUIRY"}</span>
                                <span>{isFraud ? "5%" : "1%"}</span>
                            </div>
                            <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                                <div className="h-full bg-white/10" style={{ width: isFraud ? "5%" : "1%" }} />
                            </div>
                         </div>
                    </div>
                 </div>
            </motion.div>

            {/* --- CONNECTING DATA LINES (SVG) --- */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ transform: "translateZ(-50px)" }}>
                <defs>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(34,211,238,0)" />
                        <stop offset="50%" stopColor="rgba(34,211,238,0.3)" />
                        <stop offset="100%" stopColor="rgba(34,211,238,0)" />
                    </linearGradient>
                </defs>
                {/* Left connection */}
                <path d="M 200 300 C 300 300, 400 400, 550 400" stroke="url(#lineGradient)" strokeWidth="1" fill="none" />
                {/* Right connection */}
                <path d="M 950 300 C 850 300, 750 400, 650 400" stroke="url(#lineGradient)" strokeWidth="1" fill="none" />
            </svg>

        </motion.div>
    </section>
  );
};

// --- COMPONENTS ---

const Nav = () => {
  const [open, setOpen] = useState(false);
  const { navigate, user, logout } = useNavigation();

  return (
    <div className="sticky top-0 z-40 w-full backdrop-blur supports-[backdrop-filter]:bg-black/30 bg-black/10 border-b border-white/10">
      <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
        {/* Left: Brand */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('landing')}>
          <EasyDialLogo className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16" glow />
          <span className="font-semibold tracking-tight hover:opacity-90 transition text-white">
            EasyDial <GT>AI</GT>
          </span>
          {/* Removed: Realtime badge */}
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
            {/* Dashboard dropdown */}
            <details className="relative group">
            <summary className="list-none hover:text-white flex items-center gap-1 cursor-pointer select-none text-white/70">
                Dashboard
                <ChevronDown className="w-4 h-4 mt-[1px] transition-transform duration-200 group-open:rotate-180" />
            </summary>
            <div className="absolute left-0 mt-2 w-40 rounded-md border border-white/10 bg-[#0b0d12] p-2 shadow-lg z-50">
                <button onClick={() => navigate('dashboard')} className="block w-full text-left rounded-md px-3 py-2 text-sm hover:bg-white/10 hover:text-white transition text-white/70">Outbound</button>
                <button onClick={() => navigate('dashboard')} className="block w-full text-left rounded-md px-3 py-2 text-sm hover:bg-white/10 hover:text-white transition text-white/70">Inbound</button>
            </div>

            </details>

            
                {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
                {/* Dashboard dropdown */}

                {/* --- ADD THIS NEW BUTTON HERE --- */}
                <button 
                    onClick={() => navigate('workflow')}   // ✅ Correct (no slash) 
                    className="hover:text-sky-300 text-white/70 transition-colors"
                >
                    Automation
                </button>
                
            </div>

            <a href="#pricing" className="hover:text-sky-300 text-white/70 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-sky-300 text-white/70 transition-colors">FAQ</a>

            {/* Sign in / Start Free */}
            <div className="flex items-center gap-3 pl-4 border-l border-white/10">
                {!user ? (
                  <>
                    <Button variant="ghost" onClick={() => navigate('login')}>Sign in</Button>
                    <Button onClick={() => navigate('signup')} className="bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-black font-semibold border-0">
                      Start free
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" onClick={logout}>Sign out</Button>
                )}
            </div>
        </div>


        {/* Mobile hamburger */}
        <button
          className="md:hidden ml-auto inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white/90"
          onClick={() => setOpen(!open)}
          aria-label="Open menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      
      {/* Mobile Menu */}
      {open && (
          
          <div className="md:hidden border-t border-white/10 bg-[#05060a] p-4 space-y-4">
            <button 
                  onClick={() => { 
                    navigate('workflow'); 
                    setOpen(false); 
                  }} 
                  className="block w-full text-left text-white/70 hover:text-white py-2"
               >
                  Automation
               </button>
               <a href="#pricing" className="block text-white/70 hover:text-white">Pricing</a>
               <a href="#faq" className="block text-white/70 hover:text-white">FAQ</a>
               <div className="pt-4 flex flex-col gap-2">
                  {!user ? (
                    <>
                      <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('login'); setOpen(false); }}>Sign in</Button>
                      <Button className="w-full" onClick={() => { navigate('signup'); setOpen(false); }}>Start free</Button>
                    </>
                  ) : (
                    <Button variant="ghost" className="w-full justify-start" onClick={() => { logout(); setOpen(false); }}>Sign out</Button>
                  )}
               </div>
          </div>
      )}
    </div>
  );
};

const Hero = () => {
  const { navigate } = useNavigation();
  const bars = new Array(48).fill(0);
  return (
    <section className="relative overflow-hidden">
      <Glow className="bg-[radial-gradient(50%_50%_at_50%_50%,rgba(168,85,247,0.25),transparent_60%)]" />
      <div className="mx-auto max-w-7xl px-4 py-24 sm:py-28">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <motion.h1 initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-white">
              Voice <GT>Agents</GT> that <span className="inline-flex items-center gap-2">convert<Zap className="size-6 text-yellow-400"/></span> while you sleep.
            </motion.h1>
            <p className="mt-5 text-white/80 max-w-xl">
              EasyDial AI launches, monitors, and optimizes outbound calls at scale. Realtime <span className="font-medium text-cyan-300">ASR ↔ LLM ↔ TTS</span>, timezone-aware scheduling, CRM sync, and on-call analytics.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate('signup')} className="bg-white text-black hover:bg-white/90 w-full sm:w-auto">Launch a test call</Button>
              <a href="#pricing"><Button variant="outline" className="border-white/30 text-white hover:bg-white/10 w-full sm:w-auto">View pricing</Button></a>
            </div>
            <div className="mt-6 flex items-center gap-6 text-xs text-white/60">
              <div className="flex items-center gap-2"><Shield className="size-4"/> SOC2-ready patterns</div>
              <div className="flex items-center gap-2"><Globe className="size-4"/> 70+ locales</div>
              <div className="flex items-center gap-2"><Clock className="size-4"/> Smart retry logic</div>
            </div>
          </div>

            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative hidden md:block">
            <div className="absolute -inset-6 rounded-3xl bg-gradient-to-tr from-cyan-500/25 via-fuchsia-500/15 to-emerald-400/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 opacity-40 [mask-image:radial-gradient(70%_60%_at_50%_40%,black,transparent)] bg-[repeating-linear-gradient(115deg,rgba(255,255,255,0.12)_0px,rgba(255,255,255,0.12)_2px,transparent_2px,transparent_8px)] translate-z-0" />
                <div className="absolute -inset-20 opacity-25 blur-3xl bg-[radial-gradient(60%_40%_at_50%_30%,rgba(34,211,238,.6),transparent_60%)]" />
              </div>
              <div className="relative h-[360px]">
                <div className="absolute left-1/2 top-[14%] -translate-x-1/2 w-[220px] h-[260px] opacity-65">
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 size-24 rounded-full bg-[radial-gradient(circle_at_50%_45%,rgba(255,255,255,0.9),rgba(255,255,255,0.15)_60%,transparent_70%)] blur-[2px]" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-[88px] w-[220px] h-[170px] rounded-[120px_120px_60px_60px] bg-[radial-gradient(120px_90px_at_50%_0%,rgba(255,255,255,0.7),rgba(255,255,255,0.1)_70%,transparent_80%)]" />
                  <motion.div className="absolute inset-0" animate={{ filter: ["hue-rotate(0deg)", "hue-rotate(20deg)", "hue-rotate(-10deg)", "hue-rotate(0deg)"] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
                </div>

                <div className="absolute left-1/2 top-[8%] -translate-x-1/2 size-[280px] rounded-full border border-white/20 opacity-40" />
                <div className="absolute left-1/2 top-[8%] -translate-x-1/2 size-[360px] rounded-full border border-white/10 opacity-30" />
                <div className="absolute left-1/2 top-[8%] -translate-x-1/2 size-[440px] rounded-full border border-white/5 opacity-20" />

                <div className="absolute inset-x-4 bottom-6">
                  <div className="relative mx-auto max-w-[560px] rounded-2xl border border-white/10 bg-black/30 p-4 backdrop-blur">
                    <div className="flex items-end gap-[6px] h-20 overflow-hidden">
                      {bars.map((_, i) => (
                        <motion.div key={i} className="w-[6px] rounded-sm bg-white/80" animate={{ height: ["22%", "88%", "30%", "72%", "40%"] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: i * 0.03 }} />
                      ))}
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-white/70">
                      <div className="flex items-center gap-2"><Bot className="size-4"/> 4D Agent Neural Stream</div>
                      <div>Live</div>
                    </div>
                    <div className="pointer-events-none absolute -right-8 -top-8 size-40 rounded-full border-t border-l border-white/20 animate-spin" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const TiltCard = ({ children, className = "", onSelect, selected }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rx = useSpring(y, { stiffness: 200, damping: 20 });
  const ry = useSpring(x, { stiffness: 200, damping: 20 });
  return (
    <motion.div
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const dx = e.clientX - rect.left - rect.width / 2;
        const dy = e.clientY - rect.top - rect.height / 2;
        x.set((dx / rect.width) * 10);
        y.set((-dy / rect.height) * 10);
      }}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect?.(); }}
      className={`relative rounded-2xl border border-white/10 bg-white/5 p-6 cursor-pointer outline-none focus:ring-2 focus:ring-fuchsia-400 ${className}`}
      style={{ transformStyle: "preserve-3d" }}
    >
      <motion.div style={{ transform: `perspective(900px) rotateX(${rx.get()}deg) rotateY(${ry.get()}deg)` }}>
        <div className={`absolute -inset-0.5 rounded-2xl blur transition-opacity ${selected ? "opacity-80 bg-gradient-to-r from-cyan-500/30 to-fuchsia-500/30" : "opacity-0"}`} />
        <div className="relative">{children}</div>
      </motion.div>
    </motion.div>
  );
};

const ModernTiltCard = ({ children, className = "", icon: Icon, title, colorClass = "from-cyan-500 to-cyan-400" }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rx = useSpring(0, { stiffness: 200, damping: 20 });
  const ry = useSpring(0, { stiffness: 200, damping: 20 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const dx = e.clientX - rect.left - rect.width / 2;
    const dy = e.clientY - rect.top - rect.height / 2;
    x.set(dx);
    y.set(dy);
    rx.set((-dy / rect.height) * 5);
    ry.set((dx / rect.width) * 5);
  };

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onMouseLeave={() => { x.set(0); y.set(0); rx.set(0); ry.set(0); }}
      className={`relative group h-full`}
      style={{ perspective: 1000 }}
    >
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-[#0a0b10] p-1 transition-colors hover:border-white/20"
      >
        <motion.div 
          className="pointer-events-none absolute -inset-px opacity-0 transition duration-300 group-hover:opacity-100"
          style={{
            background: useTransform(
              [x, y], 
              ([latestX, latestY]) => `radial-gradient(600px circle at ${latestX + 200}px ${latestY + 200}px, rgba(255,255,255,0.06), transparent 40%)`
            )
          }}
        />
        
        <div className="relative h-full rounded-xl bg-[#050608] p-6 sm:p-8 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)]" />
          
          <div className="relative z-10 flex flex-col h-full">
            <div className={`h-12 w-12 rounded-lg bg-gradient-to-br ${colorClass} bg-opacity-10 flex items-center justify-center mb-6 shadow-lg shadow-${colorClass.split('-')[1]}-500/20 group-hover:scale-110 transition-transform duration-300`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            
            <h3 className="text-xl font-bold mb-3 text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-white/70 transition-all">
              {title}
            </h3>
            
            <div className="text-sm text-white/50 leading-relaxed flex-grow">
              {children}
            </div>

            <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
              <div className="flex gap-1">
                <div className="h-1.5 w-1.5 rounded-full bg-white/20 group-hover:bg-emerald-500 transition-colors" />
                <div className="h-1.5 w-1.5 rounded-full bg-white/20 group-hover:bg-cyan-500 transition-colors delay-75" />
                <div className="h-1.5 w-1.5 rounded-full bg-white/20 group-hover:bg-fuchsia-500 transition-colors delay-150" />
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-mono text-white/30">
                SYS_ACTIVE
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const FeaturesSection = () => {
  return (
    <div className="bg-[#05060a] text-white py-10">
      <section className="py-32 relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-t from-fuchsia-500/5 to-transparent blur-3xl -z-10" />

        <div className="mx-auto max-w-7xl px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white">
                Engineered for Financial Resilience
              </h2>
              <p className="text-white/60 text-lg">
                Mission-critical infrastructure designed for the high-stakes environment of modern banking.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 h-auto items-stretch">
            <ModernTiltCard title="Fraud Detection" icon={Shield} colorClass="from-cyan-500 to-blue-500">
              <p>Real-time voice pattern analysis to detect synthetic identities and deepfakes.</p>
              <div className="mt-4 p-2 bg-black/40 rounded border border-white/5 font-mono text-[10px] text-cyan-300/70">
                {`> verify_voice_print(stream)`}<br/>
                {`> match_confidence: 99.8%`}
              </div>
            </ModernTiltCard>

            <ModernTiltCard title="Multi-Jurisdictional" icon={Globe} colorClass="from-fuchsia-500 to-pink-500">
              <p>Compliant with GDPR, CCPA, and regional banking regulations across 70+ countries.</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                <span className="px-2 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-white/60">GDPR</span>
                <span className="px-2 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-white/60">SOC2</span>
                <span className="px-2 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-white/60">ISO</span>
              </div>
            </ModernTiltCard>

            <ModernTiltCard title="CRM Sync" icon={Database} colorClass="from-emerald-500 to-green-500">
              <p>Bi-directional sync with Salesforce Financial Services Cloud and HubSpot Enterprise.</p>
              <div className="mt-4 p-2 bg-black/40 rounded border border-white/5 font-mono text-[10px] text-emerald-300/70">
                {`POST /api/v1/crm/leads`}<br/>
                {`Status: 200 OK`}
              </div>
            </ModernTiltCard>
          </div>
        </div>
      </section>
    </div>
  );
};

const Pricing = () => {
  const [cycle, setCycle] = useState("monthly");
  const [selected, setSelected] = useState("Growth");
  const { navigate } = useNavigation();
  
  const plans = useMemo(() => [
    { name: "Starter", priceM: 0.09, priceY: 0.07, perks: ["500 free minutes", "1 campaign", "Email support"], cta: "Start free" },
    { name: "Growth", priceM: 0.06, priceY: 0.05, perks: ["Priority ASR/LLM", "5 campaigns", "SLA & webhook logs"], cta: "Choose Growth" },
    { name: "Enterprise", priceM: "Custom", priceY: "Custom", perks: ["VPC/On-prem options", "Compliance tooling", "Dedicated SRE"], cta: "Talk to sales" },
  ], []);

  return (
    <section id="pricing" className="relative py-20">
      <Glow className="bg-[radial-gradient(50%_40%_at_80%_20%,rgba(16,185,129,0.25),transparent_70%)]" />
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Simple, elastic pricing</h2>
          <p className="text-white/70 mt-2">Pay for minutes, not promises. Scale up or down without contracts.</p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1">
            <button onClick={() => setCycle("monthly")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${cycle==="monthly"?"bg-white text-black":"text-white"}`}>Monthly</button>
            <button onClick={() => setCycle("yearly")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${cycle==="yearly"?"bg-white text-black":"text-white"}`}>Yearly</button>
          </div>
        </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const price = cycle === "monthly" ? p.priceM : p.priceY;
            const isSelected = selected === p.name;
            return (
              <TiltCard key={p.name} selected={isSelected} onSelect={() => setSelected(p.name)} className={isSelected ? "scale-[1.02]" : ""}>
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">{p.name}</div>
                  {p.name === "Growth" && <Badge className="bg-emerald-400 text-black">Popular</Badge>}
                </div>
                <div className="mt-2 text-3xl font-bold">{typeof price === "string" ? price : `$${price.toFixed(2)}/min`}</div>
                <ul className="mt-4 space-y-2 text-sm">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-center gap-2"><Check className="size-4" />{perk}</li>
                  ))}
                </ul>
                <Button onClick={() => navigate('signup')} className={`mt-6 w-full font-semibold ${isSelected ? "bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-black" : "bg-white text-black hover:bg-white/90"}`}>{p.cta}</Button>
                <div className="mt-3 text-xs text-white/60">{cycle === "yearly" && typeof price !== "string" ? "Billed as usage, discounted minute rate on annual term." : "Usage-based. No lock-in."}</div>
              </TiltCard>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FAQ = () => (
  <section id="faq" className="relative py-20">
    <div className="mx-auto max-w-4xl px-4">
      <h3 className="text-3xl font-bold text-center text-white">FAQ</h3>
      <Accordion className="mt-8">
        <AccordionItem value="item-1">
          <AccordionTrigger className="text-white">How fast can I go live?</AccordionTrigger>
          <AccordionContent>Most teams launch within a day. Import contacts, select a call type, and hit Launch. Our defaults cover retries, quiet hours, and TCPA-style guardrails.</AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-2">
          <AccordionTrigger className="text-white">Do you support multiple languages?</AccordionTrigger>
          <AccordionContent>Yes—Our stack supports 70+ locales with voice selections and regional accents.</AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-3">
          <AccordionTrigger className="text-white">What about compliance and privacy?</AccordionTrigger>
          <AccordionContent>We provide PII scrubbing, encryption at rest, audit logs, and SSO. Ask about VPC or on-prem options for regulated workloads.</AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  </section>
);

// --- PAGES ---

export function EasyDialLanding() {
  return (
    <div className="min-h-screen w-full bg-[#05060a] text-white selection:bg-fuchsia-500/40 font-sans">
      <Nav />
      <Hero />
      
      {/* --- MOVED NEURAL COMMAND CENTER HERE --- */}
      <FeaturesSection />
      <NeuralCommandCenter /> 
      
      <Pricing />
      <FAQ />
      <footer className="border-t border-white/10 py-10">
        <div className="mx-auto max-w-7xl px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-white/60 text-sm">© {new Date().getFullYear()} EasyDial AI. All rights reserved.</div>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Security</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function SignUpPage() {
  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const { navigate, login } = useNavigation();

  return (
    <div className="min-h-screen relative bg-[#05060a] text-white overflow-hidden font-sans">
      <Glow className="bg-[radial-gradient(60%_40%_at_50%_30%,rgba(34,211,238,.25),transparent_70%)]" />
      <div className="pointer-events-none absolute -inset-16 blur-3xl opacity-20 bg-[conic-gradient(from_120deg_at_60%_40%,rgba(255,255,255,0.35),transparent_35%,rgba(255,255,255,0.2))]" />
      <div className="mx-auto max-w-md w-full px-6 py-16 relative">
        <div className="text-center mb-8 cursor-pointer" onClick={() => navigate('landing')}>
          <div className="inline-flex items-center gap-2">
            <EasyDialLogo className="h-8 w-8" glow={false} />
            <div className="font-semibold">EasyDial <GT>AI</GT></div>
          </div>
          <div className="mt-2 text-sm text-white/70">Create your account</div>
        </div>
        <div className="relative rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
          <div className="grid gap-4">
            <div>
              <div className="text-sm mb-1">Full name</div>
              <Input placeholder="Ganesh Singh" className="bg-black/30 border-white/10" />
            </div>
            <div>
              <div className="text-sm mb-1">Email</div>
              <Input placeholder="you@company.com" type="email" className="bg-black/30 border-white/10" />
            </div>
            <div className="relative">
              <div className="text-sm mb-1">Password</div>
              <div className="relative">
                <Input placeholder="••••••••" type={showPass ? "text" : "password"} className="bg-black/30 border-white/10 pr-10" />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70">
                  {showPass ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="text-sm mb-1">Confirm password</div>
              <div className="relative">
                <Input placeholder="••••••••" type={showPass2 ? "text" : "password"} className="bg-black/30 border-white/10 pr-10" />
                <button type="button" onClick={() => setShowPass2((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70">
                  {showPass2 ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>
            <Button onClick={() => { login(); navigate('dashboard'); }} className="mt-2 w-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-black font-semibold border-0">Create account</Button>
            <div className="text-xs text-white/60 text-center">By continuing you agree to our Terms and Privacy Policy</div>
          </div>
        </div>
        <div className="mt-6 text-center text-sm text-white/70">
          Already have an account? <button onClick={() => navigate('login')} className="text-cyan-300 hover:text-white underline underline-offset-2">Sign in</button>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 -z-10">
        {Array.from({ length: 24 }).map((_, i) => (
          <motion.span key={i} className="absolute rounded-full bg-white/60" style={{ width: Math.random() * 2 + 1, height: Math.random() * 2 + 1, top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`, filter: "blur(0.2px)" }} animate={{ y: ["0%", "-10%", "0%"], opacity: [0.6, 1, 0.6] }} transition={{ duration: 5 + (i % 5), repeat: Infinity, ease: "easeInOut", delay: i * 0.1 }} />
        ))}
      </div>
    </div>
  );
}

export function LoginPage() {
  const [showPass, setShowPass] = useState(false);
  const { navigate, login } = useNavigation();

  return (
    <div className="min-h-screen relative bg-[#05060a] text-white overflow-hidden font-sans">
      <Glow className="bg-[radial-gradient(60%_40%_at_50%_30%,rgba(34,211,238,.25),transparent_70%)]" />
      <div className="pointer-events-none absolute -inset-16 blur-3xl opacity-20 bg-[conic-gradient(from_120deg_at_60%_40%,rgba(255,255,255,0.35),transparent_35%,rgba(255,255,255,0.2))]" />
      <div className="mx-auto max-w-md w-full px-6 py-16 relative">
        <div className="text-center mb-8 cursor-pointer" onClick={() => navigate('landing')}>
          <div className="inline-flex items-center gap-2">
            <EasyDialLogo className="h-8 w-8" glow={false} />
            <div className="font-semibold">EasyDial <GT>AI</GT></div>
          </div>
        </div>
        <div className="relative rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
          <div className="grid gap-4">
            <div>
              <div className="text-sm mb-1">Email</div>
              <Input placeholder="you@company.com" type="email" className="bg-black/30 border-white/10" />
            </div>
            <div className="relative">
              <div className="text-sm mb-1">Password</div>
              <div className="relative">
                <Input placeholder="••••••••" type={showPass ? "text" : "password"} className="bg-black/30 border-white/10 pr-10" />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70">
                  {showPass ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" className="accent-fuchsia-400" />
                Remember me
              </label>
              <a href="#" className="text-cyan-300 hover:text-white">Forgot?</a>
            </div>
            <Button onClick={() => { login(); navigate('dashboard'); }} className="mt-2 w-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-black font-semibold border-0">Sign in</Button>
            <div className="text-xs text-white/60 text-center">SSO available on Growth and Enterprise</div>
          </div>
        </div>
        <div className="mt-6 text-center text-sm text-white/70">
          New here? <button onClick={() => navigate('signup')} className="text-cyan-300 hover:text-white underline underline-offset-2">Create an account</button>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 -z-10">
        {Array.from({ length: 24 }).map((_, i) => (
          <motion.span key={i} className="absolute rounded-full bg-white/60" style={{ width: Math.random() * 2 + 1, height: Math.random() * 2 + 1, top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`, filter: "blur(0.2px)" }} animate={{ y: ["0%", "-10%", "0%"], opacity: [0.6, 1, 0.6] }} transition={{ duration: 5 + (i % 5), repeat: Infinity, ease: "easeInOut", delay: i * 0.1 }} />
        ))}
      </div>
    </div>
  );
}

// --- DASHBOARD GATE ---

const DashboardGate = () => {
  const { user, navigate } = useNavigation();
  
  useEffect(() => {
    if (!user) {
       const timer = setTimeout(() => navigate('login'), 1500);
       return () => clearTimeout(timer);
    }
  }, [user, navigate]);

  if (!user) {
    return (
        <div className="min-h-screen w-full bg-[#05060a] flex flex-col items-center justify-center text-white/70 font-mono gap-4">
            <Activity className="animate-spin w-8 h-8 text-cyan-400" />
            <div>Redirecting to Login...</div>
        </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#05060a] flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center text-white/70 font-mono flex-col gap-4">
            <div className="text-2xl font-bold text-white">Welcome Back</div>
            <div className="text-sm">User ID: {user.id}</div>
            <div className="p-4 border border-white/10 rounded bg-white/5">
                Dashboard Content Placeholder
            </div>
            <Button onClick={() => navigate('landing')}>Return Home</Button>
        </div>
    </div>
  );
};

// --- APP ENTRY POINT ---

function App() {
  // Simple state-based routing for single-file environment
const [currentView, setCurrentView] = useState(() => {
    const path = window.location.pathname.replace('/', '');
    // If the URL matches a known page, go there. Otherwise go to landing.
    return ['workflow', 'login', 'signup', 'dashboard'].includes(path) ? path : 'landing';
  });
    const [user, setUser] = useState(null);

  
  const navigate = (view) => {
      window.scrollTo(0, 0);
      setCurrentView(view);
  };

  const login = () => setUser({ id: 'user_123', name: 'Demo User' });
  const logout = () => {
      setUser(null);
      navigate('landing');
  };

  // Add this inside function App() { ... }
  
  // 1. Update URL when view changes
  useEffect(() => {
    const path = currentView === 'landing' ? '/' : `/${currentView}`;
    window.history.pushState(null, '', path);
  }, [currentView]);

  // 2. Handle Browser "Back" Button
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.replace('/', '');
      setCurrentView(path || 'landing');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <NavigationContext.Provider value={{ currentView, navigate, user, login, logout }}>
        {currentView === 'landing' && <EasyDialLanding />}
        {currentView === 'login' && <LoginPage />}
        {currentView === 'signup' && <SignUpPage />}
        {currentView === 'dashboard' && <DashboardGate />}
        {currentView === 'workflow' && <WorkflowMap />}
    </NavigationContext.Provider>
  );
}

export default App;
